import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatOptionSelectionChange } from '@angular/material/core';
import { Language, LanguageService } from 'app/core/language.service';

interface ContinentCountry {
  code: string;
  name_sv: string;
  name_en: string;
  dialCode: string;
}

interface ContinentGroup {
  key: string;
  name_en: string;
  name_sv: string;
  countries: ContinentCountry[];
}

interface DisplayItem {
  type: 'continent' | 'country' | 'selected-header';
  key: string;
  value: string;
  isExpanded?: boolean;
}

export interface CountryOptionGroup {
  continent: string;
  continentLabel: string;
  countries: Array<{ key: string; value: string }>;
}

@Component({
  selector: 'app-country-select',
  templateUrl: './country-select.component.html',
  styleUrls: ['./country-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CountrySelectComponent implements OnInit {
  @Input() public value: string[] = [];
  @Output() public valueChange = new EventEmitter<string[]>();
  @Input() public required = false;
  @Input() public placeholder = '';

  @ViewChild('searchInput') private readonly searchInput: ElementRef<HTMLInputElement>;

  private readonly http = inject(HttpClient);
  private readonly languageService = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly searchControl = new FormControl('');
  protected readonly countriesControl = new FormControl<string[]>([]);

  private readonly searchQuery = signal('');
  protected readonly loading = signal(false);
  protected readonly groups = signal<CountryOptionGroup[]>([]);
  protected readonly expandedContinents = signal<Set<string>>(new Set());
  private readonly selectedValues = signal<string[]>([]);

  protected readonly displayItems = computed((): DisplayItem[] => {
    const query = this.searchQuery().trim().toLowerCase();
    const groups = this.groups();
    const selected = new Set(this.selectedValues());
    const items: DisplayItem[] = [];

    // Always show selected countries at the top
    if (selected.size > 0) {
      const allCountries = groups.flatMap((g) => g.countries);
      const selectedCountries = allCountries.filter((c) => selected.has(c.key));
      if (selectedCountries.length > 0) {
        items.push({ type: 'selected-header', key: '__selected__', value: '' });
        for (const country of selectedCountries) {
          items.push({ type: 'country', key: country.key, value: country.value });
        }
      }
    }

    if (query.length > 0) {
      for (const group of groups) {
        for (const country of group.countries) {
          if (!selected.has(country.key) && country.value.toLowerCase().includes(query)) {
            items.push({ type: 'country', key: country.key, value: country.value });
          }
        }
      }
      return items;
    }

    const expanded = this.expandedContinents();
    for (const group of groups) {
      const isExpanded = expanded.has(group.continent);
      items.push({ type: 'continent', key: group.continent, value: group.continentLabel, isExpanded });
      if (isExpanded) {
        for (const country of group.countries) {
          if (!selected.has(country.key)) {
            items.push({ type: 'country', key: country.key, value: country.value });
          }
        }
      }
    }
    return items;
  });

  protected readonly selectedCount = computed(() => this.selectedValues().length);

  protected readonly selectedLabel = computed(() => {
    const values = this.selectedValues();
    if (values.length === 0) return '';
    if (values.length <= 10) {
      return values.join(', ');
    }
    return '';
  });

  ngOnInit(): void {
    this.countriesControl.setValue(this.value ?? [], { emitEvent: false });
    this.selectedValues.set(this.value ?? []);

    this.searchControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((v) => {
      this.searchQuery.set(v ?? '');
    });

    this.countriesControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((values) => {
      const newValues = values ?? [];
      this.selectedValues.set(newValues);
      this.value = newValues;
      this.valueChange.emit(this.value);
    });

    this.loadCountries();
  }

  protected onPanelOpenedChange(isOpen: boolean): void {
    if (isOpen) {
      // Defer focus so the panel is fully rendered
      setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    } else {
      this.searchControl.setValue('');
    }
  }

  protected onContinentSelect(continent: string, event: MatOptionSelectionChange): void {
    if (!event.isUserInput) return;
    event.source.deselect();
    this.toggleContinent(continent);
  }

  protected toggleContinent(continent: string): void {
    const current = new Set(this.expandedContinents());
    if (current.has(continent)) {
      current.delete(continent);
    } else {
      current.add(continent);
    }
    this.expandedContinents.set(current);
  }

  private async loadCountries(): Promise<void> {
    this.loading.set(true);
    const currentLanguage = await this.languageService.getCurrentLanguageAsync();
    const nameField = currentLanguage === Language.Swedish ? 'name_sv' : 'name_en';

    this.http
      .get<ContinentGroup[]>('/assets/countries/continents_countries.json')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          const result: CountryOptionGroup[] = [];
          const allContinents = new Set<string>();
          for (const group of data) {
            const continentLabel = currentLanguage === Language.Swedish ? group.name_sv : group.name_en;
            const sorted = group.countries
              .map((c) => ({ key: c.code, value: c[nameField] }))
              .sort((a, b) => a.value.localeCompare(b.value));
            result.push({ continent: group.key, continentLabel, countries: sorted });
            allContinents.add(group.key);
          }
          this.groups.set(result);
          this.expandedContinents.set(allContinents);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }
}
